import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { create } from "../../api/API";
import { GoogleLogin } from "@react-oauth/google";
import { FaEye, FaEyeSlash, FaTimesCircle } from "react-icons/fa";
import FloatingLabelInput from "../Generic/FloatingLabelInput";
import { loginUser } from "../../utility/UserUtils";

const LoginForm = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [unverified, setUnverified] = useState(false);
  const [paymentRequired, setPaymentRequired] = useState(false);

  const navigate = useNavigate();

  const validate = () => {
    if (!email || !password) return "Email and password are required";
    if (password.length < 6) return "Password must be at least 6 characters";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errMsg = validate();
    if (errMsg) {
      toast.error(errMsg);
      return;
    }

    try {
      setLoading(true);

      const res = await create("/member-login", {
        email,
        password,
      });

      loginUser({ ...res.user, token: res.token }); //this line added for member email login

      if (onLogin) {
        onLogin({ ...res.user, token: res.token });
      }

      window.dispatchEvent(new Event("authChange"));
      toast.success("Logged in successfully!");
      navigate("/");
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Login failed";

      if (message === "EMAIL_NOT_VERIFIED") {
        toast.error("Please verify your email before logging in.");
        setUnverified(true);
        setPaymentRequired(false);
        return;
      }

      if (message === "PAYMENT_REQUIRED") {
        toast.error("Please complete payment to activate your account.");
        setPaymentRequired(true);
        setUnverified(false);
        return;
      }

      if (message === "ACCOUNT_INACTIVE") {
        toast.error("Your account is inactive. Please contact support.");
        return;
      }

      toast.error(message);
    }
  };
  const handleResendVerification = async () => {
    if (!email) {
      toast.error("Please enter your email first.");
      return;
    }

    try {
      await create("/resend-verification-email", { email });
      toast.success("Verification email resent. Please check your inbox.");
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Failed to resend verification email.";
      toast.error(message);
    }
  };

  const handleCompletePayment = async () => {
    if (!email) {
      toast.error("Please enter your email first.");
      return;
    }

    try {
      const res = await create("/retry-payment", { email });

      toast.success("Redirecting to payment...");
      window.location.href = res.paymentUrl;
    } catch (err) {
      const message =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Failed to regenerate payment link";

      toast.error(message);
    }
  };

  const handleGoogleLoginSuccess = async (credentialResponse) => {
    try {
      const token = credentialResponse.credential;
      const res = await create("/auth/google-login", { token });

      loginUser({ ...res.user, token: res.token });

      if (onLogin) {
        onLogin({ ...res.user, token: res.token });
      }

      window.dispatchEvent(new Event("authChange"));
      toast.success("Logged in with Google!");
      navigate("/");
    } catch (err) {
      const status = err.response?.status;
      const message =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Google login failed";

      if (message === "PAYMENT_REQUIRED") {
        toast.error("Please complete payment to activate your account.");
        setPaymentRequired(true);
        setUnverified(false);

        if (err.response?.data?.email) {
          setEmail(err.response.data.email);
        }

        return;
      }

      if (message === "ACCOUNT_INACTIVE") {
        toast.error("Your account is inactive. Please contact support.");
        return;
      }

      toast.error(message);

      if (status === 404) {
        navigate("/signup", {
          state: { token: credentialResponse.credential, via: "google" },
        });
      }
    }
  };

  return (
    // <div className="auth-page">
    <div className="auth-box1 bg-white">
      <h2 className="auth-title text-center">Have an account? Login</h2>
      <p className="auth-subtext text-black text-center">
        *Required fields are marked with an asterisk
      </p>

      <form onSubmit={handleSubmit}>
        {/* Email */}
        <div className="input-wrapper bg-white">
          <FloatingLabelInput
            label="Email*"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {email && (
            <span className="clear-icon" onClick={() => setEmail("")}>
              <FaTimesCircle />
            </span>
          )}
        </div>

        {/* Password */}
        <div className="input-wrapper bg-white">
          <FloatingLabelInput
            label="Password*"
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <span
            className="password-icon-login"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <FaEyeSlash /> : <FaEye />}
          </span>
          {password && (
            <span className="clear-icon" onClick={() => setPassword("")}>
              <FaTimesCircle />
            </span>
          )}
        </div>

        {/* Links */}
        <div className="auth-links flex justify-between mt-2">
          <p
            className="text-blue-600 underline clickable-text"
            onClick={() => navigate("/reset-password")}
          >
            Forgot Password?
          </p>

          {unverified && (
            <p
              className="text-blue-600 underline clickable-text"
              onClick={handleResendVerification}
            >
              Resend Verification Email
            </p>
          )}

          {paymentRequired && (
            <p
              className="text-blue-600 underline clickable-text"
              onClick={handleCompletePayment}
            >
              Complete Payment
            </p>
          )}
        </div>

        <button
          type="submit"
          className="auth-btn bg-primary text-white"
          disabled={loading}
        >
          {loading ? "Logging in..." : "Login"}
        </button>
      </form>

      {/* Divider */}
      <div className="auth-divider">
        <span>or</span>
      </div>

      {/* Google Login */}
      <div className="auth-socials">
        <GoogleLogin
          onSuccess={handleGoogleLoginSuccess}
          onError={() => toast.error("Google login failed")}
        />
      </div>

      <p className="auth-footer">Don’t have an account yet?</p>
      <p>
        <Link to="/signup">Join today</Link>
      </p>
    </div>
    // </div>
  );
};

export default LoginForm;
